import { SagaIterator } from 'redux-saga';
import {
  call,
  put,
  select,
  takeEvery,
  all,
  throttle,
  fork,
} from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { PapiDeviation } from '@wix/da-papi-types';
import { isApiError } from '@wix/da-http-client';
import {
  selectors,
  withCursor,
  actions,
} from '@wix/da-shared-react/pkg/Stream';
import { FetchType } from '@wix/da-shared-react/pkg/Stream/base/types';
import { initializeThread } from '@wix/da-shared-react/pkg/CommentThread/actionCreators';
import { getCommentableTypeForDeviationType } from '@wix/da-shared-react/pkg/DeviationPage/redux/helpers';
import {
  PAGE_STREAM_ID,
  SAGA_FETCH_NEXT_DELAY_DEFAULT,
  SAGA_FETCH_NEXT_DELAY_BY_PAGE,
} from '../../constants';
import {
  AnyPapiDaBrowseNetworkbarRequest,
  AnyPapiDaBrowseNetworkbarResponse,
} from '../../types/api';
import { apiGet } from '../helpers/xhr';
import {
  initPageData,
  initPostsFeed,
  fetchPageStreamNext,
} from '../actions/app';
import { getCurrentFeedType, getCurrentPageType } from '../selectors/page';
import { BrowseStreamParams } from '../types/stream';
import { getNormalizedStreamItems } from '../helpers/normalize';

const logger = ddt.createLogger('onscrollbottom');

const FEED_COMMENTS_MAX_DEPTH = 1;
const FEED_COMMENTS_ITEMS_PER_FETCH = 10;

function* initializePostThreads(deviations: PapiDeviation[]) {
  const commentThreads = deviations.map(deviation =>
    put(
      initializeThread({
        thread: {
          commentableItemId: deviation.deviationId,
          commentableTypeId: getCommentableTypeForDeviationType(deviation),
          items: [],
          hasMore: !!deviation.stats?.comments,
          total: deviation.stats?.comments ?? 0,
          canPostComment: !!deviation.isCommentable,
        },
        maxDepth: FEED_COMMENTS_MAX_DEPTH,
        itemsPerFetch: FEED_COMMENTS_ITEMS_PER_FETCH,
      })
    )
  );
  yield all(commentThreads);
}

function* initPageStream({ payload }: ReturnType<typeof initPageData>) {
  const {
    initData,
    initData: {
      hasMore,
      usedLimit = 10,
      nextCursor,
      prevCursor,
      currentOffset = 0,
    },
    requestParams: originalRequestParams,
    requestEndpoint,
    feedType,
  } = payload;

  const streamId = PAGE_STREAM_ID;
  const { items, itemType, entities } = getNormalizedStreamItems(initData);
  const { page, cursor, init, ...requestParams } = originalRequestParams as any;

  const streamParams: BrowseStreamParams = {
    itemType,
    requestParams,
    requestEndpoint,
    initialOffset: currentOffset,
  };

  yield put(
    withCursor.actions.initialize({
      streamId,
      itemsPerFetch: usedLimit,
      items,
      hasMore,
      hasLess: !!prevCursor,
      cursor: nextCursor,
      prevCursor,
      streamParams,
      entities,
      fetchNextCallback: fetchPageStreamNext,
    })
  );

  if (feedType === 'posts' && initData.hasOwnProperty('deviations')) {
    yield call(initializePostThreads, initData['deviations']);
    yield put(initPostsFeed(initData['deviations']));
  }
}

function* onFetchPageStreamNext({
  payload: { streamId },
}: ReturnType<typeof fetchPageStreamNext>) {
  const { requestParams, requestEndpoint }: BrowseStreamParams = yield select(
    selectors.getStreamParams,
    streamId
  );
  const cursor = yield select(withCursor.selectors.getCursor, streamId);

  if (!cursor) {
    return;
  }

  const params: AnyPapiDaBrowseNetworkbarRequest = {
    ...requestParams,
    cursor,
  };

  // routing for requests...
  let endpoint = `/networkbar/${requestEndpoint}`;
  if (
    requestEndpoint.startsWith('search/') ||
    requestEndpoint.startsWith('marketplace/')
  ) {
    endpoint = requestEndpoint;
  }

  const result: AnyPapiDaBrowseNetworkbarResponse | false = yield call(
    apiGet,
    endpoint,
    params
  );

  if (!result || isApiError(result)) {
    yield put(
      withCursor.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: "Can't load deviations",
      })
    );
    return;
  }

  const { hasMore = false, nextCursor, serverResultsChanged } = result;
  const { items, entities } = getNormalizedStreamItems(result);

  yield put(
    withCursor.actions.fetchSuccess({
      streamId,
      items,
      entities,
      hasMore,
      cursor: nextCursor,
    })
  );

  if (serverResultsChanged) {
    yield put(
      actions.setParam({
        streamId,
        paramType: 'serverResultsChanged',
        paramValue: serverResultsChanged,
      })
    );
  }

  const feedType = yield select(getCurrentFeedType);
  if (feedType === 'posts' && result.hasOwnProperty('deviations')) {
    yield call(initializePostThreads, result['deviations']);
    yield put(initPostsFeed(result['deviations']));
  }
}

function* onFetchPageStreamNextThrottled(): SagaIterator {
  const [pageType, feedType = 'deviations'] = yield all([
    select(getCurrentPageType),
    select(getCurrentFeedType),
  ]);

  const delayMs =
    SAGA_FETCH_NEXT_DELAY_BY_PAGE[pageType]?.[feedType] ??
    SAGA_FETCH_NEXT_DELAY_DEFAULT;
  logger.log(
    'onFetchPageStreamNextThrottled',
    JSON.stringify({ pageType, feedType, delayMs })
  );
  yield throttle(delayMs, fetchPageStreamNext, onFetchPageStreamNext);
}

export default function* browseStreamsRootSaga(): SagaIterator {
  yield all([
    fork(onFetchPageStreamNextThrottled),
    takeEvery(initPageData, initPageStream),
  ]);
}
